import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTransactions } from 'store/transactions/useTransactions';
import { IRegisterVaultFactoryForm } from 'components/onboarding/deployment/type';
import { updateVaultFactorySchema } from 'components/onboarding/deployment/formSchema';
import { FormError } from 'components/onboarding/form/FormError';
import {
  Heading,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Text
} from '@chakra-ui/react';
import {
  mapPowerSwitchStatusToDescription,
  PowerSwitchStatus
} from 'api/blockchain/rewardProgram';
import { FC } from 'react';
import { ethers } from 'ethers';

type Props = {
  programState?: PowerSwitchStatus;
  aludelAbi: ethers.ContractInterface;
};

export const RegisterVaultFactoryForm: FC<Props> = ({
  programState,
  aludelAbi
}) => {
  const { updateVaultFactory } = useTransactions();
  const { rewardProgramAddress } =
    useParams<{ rewardProgramAddress: string }>();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<IRegisterVaultFactoryForm>({
    resolver: yupResolver(updateVaultFactorySchema),
    mode: 'onBlur',
    defaultValues: {
      address: ''
    }
  });

  const updateVaultFactoryAction = async ({ address }: { address: string }) => {
    await updateVaultFactory(
      rewardProgramAddress,
      address,
      'register',
      aludelAbi
    );
  };

  return (
    <>
      <Heading size='md' mb={6}>
        Register Vault Factory
      </Heading>
      <Text color='gray.200' mb={6}>
        This option whitelists users owning a vault from the specified factory,
        allowing them to stake to this program. A vault factory is, for example,
        the Crucible factory.
      </Text>
      <Stack
        direction={['column', 'row']}
        alignItems={['flex-start', 'flex-end']}
        spacing={4}
      >
        <FormControl width='max-content' isRequired>
          <FormLabel>Enter Address</FormLabel>
          <Input
            id='address'
            type='text'
            borderColor={'gray.300'}
            placeholder='0x'
            {...register('address')}
          />
          {errors.address && <FormError>{errors.address.message}</FormError>}
        </FormControl>
        <Button
          px={6}
          type='submit'
          borderRadius={'lg'}
          disabled={programState === PowerSwitchStatus.SHUTDOWN}
          onClick={handleSubmit((d) => updateVaultFactoryAction(d))}
        >
          {programState === PowerSwitchStatus.SHUTDOWN
            ? mapPowerSwitchStatusToDescription[programState]
            : 'Register Vault Factory'}
        </Button>
      </Stack>
    </>
  );
};
